import DownloadExcelButton from '../Buttons/DownloadExcelButton';
import { usePreferredUserLocation } from '../../hooks';
import type { Dayjs } from '@utils/dayjs';
import dayjs from '@utils/dayjs';
import { DATE_FORMATS } from '@callmart/types';
import { getAllDatesInAMonth, getAllTimeSlotsInADay } from '../../utils/dateUtils';
import { getTotalsWithWorkingUnits } from '../../services/totals';
import { groupBy } from 'lodash';
import { STATIC_EXCEL_COLUMN_NAMES } from './helpers/utils';
import { getTotalsTypeByValue } from '../../enums/TotalsType';
import { getWorkingUnitShortName } from '../../utils/workingUnitUtils';
import { appTimezone } from '../../config';
import { FC } from 'react';

const getColumnNames = (daysInMonth: Dayjs[]) => {
    const columnNames = [...STATIC_EXCEL_COLUMN_NAMES];

    for (const day of daysInMonth) {
        columnNames.push(dayjs(day).format(DATE_FORMATS.DATE));
    }
    return columnNames;
};

interface OSPExportTotalsButtonProps {
    date: Dayjs;
    location: string | number;
    totalsType: 'bidding' | 'corrected';
}

const OSPExportTotalsButton: FC<OSPExportTotalsButtonProps> = ({ date, location: selectedLocation, totalsType }) => {
    const [location] = usePreferredUserLocation(selectedLocation);

    const daysInMonth = getAllDatesInAMonth(date);
    const columnNames = getColumnNames(daysInMonth);
    const timeSlotsInADay = getAllTimeSlotsInADay();

    const fileName = `Totals (${getTotalsTypeByValue(totalsType).nameOSP})_${location?.city}_${dayjs(date).format(
        'MM.YYYY'
    )}.xlsx`;

    const getXLSXSheets = async () => {
        const { workingUnits, totals } = await getTotalsWithWorkingUnits({
            dateFrom: dayjs(date).tz(appTimezone).startOf('month'),
            dateTo: dayjs(date).tz(appTimezone).endOf('month'),
            location: Number(selectedLocation),
            totalsType,
        });

        const totalsByWorkingUnit = groupBy(totals, 'workingUnitLocationId');
        const sheetsData = [];
        for (let i = 0; i < workingUnits.length; i++) {
            const workingUnit = workingUnits[i];
            const sheetData = [];
            const totals = totalsByWorkingUnit[workingUnit.workingUnitLocationId];
            for (const timeSlot of timeSlotsInADay) {
                const row = [
                    location?.code,
                    location?.city,
                    workingUnit.worktype,
                    workingUnit.channel,
                    workingUnit.task,
                    timeSlot,
                ];
                const totalsInTimeSlot = totals
                    ? totals.filter((t) => t.offeredReport.slotId.split('-')[1] === timeSlot + ':00')
                    : [];

                for (const day of daysInMonth) {
                    const total = totalsInTimeSlot.find(
                        (t) => t.offeredReport.slotId.split('-')[0] === day.format(DATE_FORMATS.DATE)
                    );

                    row.push(total ? total.total : '');
                }
                sheetData.push(row);
            }

            sheetsData.push({
                sheetName: getWorkingUnitShortName(workingUnit),
                sheetData,
            });
        }

        return sheetsData;
    };

    return (
        <DownloadExcelButton
            title="Totals"
            fileName={fileName}
            location={selectedLocation}
            columnNames={columnNames}
            getSheetsData={() => getXLSXSheets()}
            buttonText=""
            showFileIcon={true}
            columnsToHide={[]}
            buttonProps={undefined}
        />
    );
};

export default OSPExportTotalsButton;
